import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Users, Coins, Gift, Euro, Megaphone, Flame, TrendingUp, Cake, Send, Ticket, Percent, Store } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import BackupStatusCard from "../components/BackupStatusCard";
import { useAuth } from "../context/AuthContext";

const TINTS = {
  blue: { card: "dash-tint dash-tint-blue", icon: "bg-[color:var(--vivi)] text-white" },
  sky: { card: "dash-tint dash-tint-sky", icon: "bg-sky-500 text-white" },
  mint: { card: "dash-tint dash-tint-mint", icon: "bg-emerald-500 text-white" },
  violet: { card: "dash-tint dash-tint-violet", icon: "bg-violet-500 text-white" },
  amber: { card: "dash-tint dash-tint-amber", icon: "bg-amber-500 text-white" },
  rose: { card: "dash-tint dash-tint-rose", icon: "bg-rose-500 text-white" },
};

const Stat = ({ label, value, icon: Icon, accent = "blue", suffix = "", testid }) => {
  const t = TINTS[accent] || TINTS.blue;
  return (
    <div className={`card-elev p-5 ${t.card}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</span>
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center shadow-md ${t.icon}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="metric-value text-3xl sm:text-4xl">
        {value}<span className="text-lg text-zinc-500 ml-1">{suffix}</span>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [birthdays, setBirthdays] = useState([]);
  const [bonus, setBonus] = useState(50);

  const loadBirthdays = () => {
    api.get("/birthdays/today").then((r) => setBirthdays(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
    loadBirthdays();
  }, []);

  const celebrate = async (c) => {
    if (!window.confirm(
      `Confermi il bonus compleanno di ${bonus} pt a ${c.name}?\n` +
      `Saldo attuale: ${c.points ?? 0} pt · Nuovo saldo: ${(c.points ?? 0) + bonus} pt`
    )) return;
    try {
      const { data } = await api.post("/birthdays/celebrate", { customer_id: c.id, bonus_points: bonus });
      toast.success(`+${data.bonus_awarded} pt regalati a ${c.name} 🎂`);
      window.open(data.wa_link, "_blank");
      loadBirthdays();
      const s = await api.get("/stats/dashboard");
      setStats(s.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!stats) return <div className="text-zinc-500">Caricamento…</div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="pill pill-gold mb-3">CONSOLE OWNER</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
            Il tuo Circuito, in numeri.
          </h1>
          <p className="text-zinc-400 mt-1">
            Panoramica giornaliera della raccolta fedeltà
            {stats.branches_analyzed != null && (
              <> · <span className="inline-flex items-center gap-1 font-semibold text-[color:var(--vivi)]" data-testid="dashboard-branches-analyzed">
                <Store className="h-3.5 w-3.5" /> {stats.branches_analyzed} {stats.branches_analyzed === 1 ? "punto vendita analizzato" : "punti vendita analizzati"}
                {stats.branches_total > stats.branches_analyzed ? ` su ${stats.branches_total}` : ""}
              </span></>
            )}
          </p>
        </div>
        <Link
          to="/banco"
          data-testid="dashboard-open-counter"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] transition font-semibold"
        >
          <Flame className="h-4 w-4" /> Apri Negozio
        </Link>
      </div>

      {stats.active_campaign && (
        <div className="card-elev p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 dash-tint dash-tint-sky" data-testid="active-campaign-banner">
          <div className="flex items-center gap-3">
            <Megaphone className="h-5 w-5 text-amber-300" />
            <div>
              <div className="font-heading font-bold text-lg">{stats.active_campaign.name}</div>
              <div className="text-sm text-zinc-400">{stats.active_campaign.description || "Campagna in corso"}</div>
            </div>
          </div>
          <span className="pill pill-gold text-base tracking-wide">{stats.active_campaign.multiplier}× PUNTI</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-branches" label="Punti vendita analizzati" value={stats.branches_analyzed ?? "—"} icon={Store} accent="blue" suffix={stats.branches_total > (stats.branches_analyzed ?? 0) ? `/ ${stats.branches_total}` : ""} />
        <Stat testid="stat-customers" label="Clienti fidelizzati" value={stats.total_customers} icon={Users} accent="sky" />
        <Stat testid="stat-earned" label="Punti emessi (tot)" value={stats.total_points_earned} icon={Coins} accent="amber" />
        <Stat testid="stat-redeemed" label="Punti riscattati" value={stats.total_points_redeemed} icon={Gift} accent="mint" />
        <Stat testid="stat-revenue" label="Fatturato tracciato" value={`€ ${stats.total_revenue}`} icon={Euro} accent="blue" />
        <Stat testid="stat-coupons-used" label="Coupon usati (mese)" value={stats.coupons_used_month || 0} icon={Ticket} accent="violet" />
        <Stat testid="stat-coupons-discount-total" label="Sconto coupon (mese)" value={`€ ${(stats.coupons_discount_total_month || 0).toFixed(2)}`} icon={Percent} accent="rose" />
        <Stat testid="stat-coupons-discount-avg" label="Sconto medio coupon" value={`€ ${(stats.coupons_discount_avg_month || 0).toFixed(2)}`} icon={Percent} accent="sky" />
        <Stat testid="stat-active-rewards" label="Premi in catalogo" value={stats.active_rewards} icon={Gift} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-elev p-5 lg:col-span-2 dash-tint dash-tint-blue">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-semibold text-lg">Andamento 14 giorni</h3>
              <p className="text-xs text-zinc-500">Punti emessi vs. riscattati</p>
            </div>
            <TrendingUp className="h-5 w-5 text-emerald-300" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
              <AreaChart data={stats.series}>
                <defs>
                  <linearGradient id="e" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#1E40AF" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#1E40AF" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="r" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 10 }}
                  labelStyle={{ color: "#475569" }}
                />
                <Area type="monotone" dataKey="earned" stroke="#1E40AF" fill="url(#e)" strokeWidth={2} />
                <Area type="monotone" dataKey="redeemed" stroke="#10B981" fill="url(#r)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {isAdmin && <BackupStatusCard />}
        <div className="card-elev p-5 dash-tint dash-tint-rose">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Cake className="h-5 w-5 text-rose-500" />
              <h3 className="font-heading font-semibold text-lg">Compleanni di oggi</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">bonus</span>
              <Input
                data-testid="birthday-bonus-input"
                type="number"
                min="0"
                value={bonus}
                onChange={(e) => setBonus(parseInt(e.target.value || "0"))}
                className="w-16 h-8 bg-zinc-950 border-zinc-800 text-sm text-center"
              />
              <span className="text-[10px] text-zinc-500">pt</span>
            </div>
          </div>
          <ul className="space-y-3 max-h-72 overflow-y-auto">
            {birthdays.length === 0 && (
              <li className="text-sm text-zinc-500 py-4 text-center">Nessun compleanno oggi 🎈</li>
            )}
            {birthdays.map((c) => (
              <li key={c.id} className="flex items-center gap-3" data-testid={`birthday-row-${c.id}`}>
                <span className="h-9 w-9 rounded-lg bg-pink-500/10 text-pink-300 flex items-center justify-center">
                  <Cake className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{c.phone}</div>
                </div>
                {c.celebrated_today ? (
                  <span className="pill pill-mint">Fatto</span>
                ) : (
                  <Button
                    data-testid={`birthday-send-${c.id}`}
                    size="sm"
                    onClick={() => celebrate(c)}
                    className="bg-[color:var(--whatsapp)] text-black hover:opacity-90 h-8"
                  >
                    <Send className="h-3 w-3 mr-1" /> Regala {bonus}pt
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="card-elev p-5 dash-tint dash-tint-amber">
          <h3 className="font-heading font-semibold text-lg mb-4">Top clienti per punti</h3>
          <ul className="space-y-3">
            {stats.top_customers.length === 0 && (
              <li className="text-sm text-zinc-500">Nessun cliente ancora registrato.</li>
            )}
            {stats.top_customers.map((c, i) => (
              <li key={c.id} className="flex items-center gap-3">
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center font-heading font-bold text-sm
                  ${i === 0 ? "bg-amber-400 text-white" :
                    i === 1 ? "bg-sky-400 text-white" :
                    "bg-[color:var(--vivi)] text-white"}`}>
                  #{i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">Carta {c.card_number}</div>
                </div>
                <span className="pill pill-gold font-mono">{c.points} pt</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
